import { ReactElement, ReactNode, useEffect } from 'react'
import produce from 'immer'
import { cloneDeep, reject, remove } from 'lodash-es'
import create from 'zustand'
import { combine } from 'zustand/middleware'
import { validate } from '../middleware'

interface Props {
  children: ReactNode
  tensileTransmissionGraph: TensileTransmissionGraph
  verticalTransmissionGraph: VerticalTransmissionGraph
}

interface TransmissionInitialState {
  tensileTransmissionGraph: TensileTransmissionGraph
  verticalTransmissionGraph: VerticalTransmissionGraph
}

interface TransmisionStoreType extends TransmissionInitialState {
  setTensileTransmissionGraph: (data: TensileTransmissionGraph) => void
  setVerticalTransmissionGraph: (data: VerticalTransmissionGraph) => void
  deleteVerticalTransmitter: (targetGuid: string) => void
  clear: () => void
}

const initialState: TransmissionInitialState = {
  tensileTransmissionGraph: {
    support_targets: [],
    element_supports: [],
    element_targets: [],
  },
  verticalTransmissionGraph: {
    support_targets: [],
    element_supports: [],
    element_targets: [],
  },
}

const createStore = () =>
  create<TransmisionStoreType>(
    validate(
      combine(cloneDeep(initialState), set => ({
        clear: () => set(cloneDeep(initialState)),

        setTensileTransmissionGraph: (data: TensileTransmissionGraph) =>
          set(
            produce(state => {
              state.tensileTransmissionGraph = data
            }),
          ),

        setVerticalTransmissionGraph: (data: VerticalTransmissionGraph) =>
          set(
            produce(state => {
              state.verticalTransmissionGraph = data
            }),
          ),

        deleteVerticalTransmitter: (targetGuid: string) =>
          set(
            produce(state => {
              state.verticalTransmissionGraph.element_targets = reject(
                state.verticalTransmissionGraph.element_targets,
                ['guid', targetGuid],
              )

              const [supportTarget] = remove(state.verticalTransmissionGraph.support_targets, [
                'target_guid',
                targetGuid,
              ]) as unknown as ElementSupportTarget[]

              if (supportTarget) {
                state.verticalTransmissionGraph.element_supports = reject(
                  state.verticalTransmissionGraph.element_supports,
                  ['guid', supportTarget.support_guid],
                )
              }
            }),
          ),
      })),
    ),
  )

const useTransmissionStore = createStore()

const TransmissionStoreProvider = ({
  children,
  tensileTransmissionGraph,
  verticalTransmissionGraph,
}: Props): ReactElement => {
  const clear = useTransmissionStore(state => state.clear)
  const setTensileTransmissionGraph = useTransmissionStore(
    state => state.setTensileTransmissionGraph,
  )
  const setVerticalTransmissionGraph = useTransmissionStore(
    state => state.setVerticalTransmissionGraph,
  )

  useEffect(() => {
    setTensileTransmissionGraph(tensileTransmissionGraph)
  }, [setTensileTransmissionGraph, tensileTransmissionGraph])

  useEffect(() => {
    setVerticalTransmissionGraph(verticalTransmissionGraph)
  }, [setVerticalTransmissionGraph, verticalTransmissionGraph])

  useEffect(() => {
    return () => clear()
  }, [clear])

  return <>{children}</>
}

export { TransmissionStoreProvider, useTransmissionStore }
